import { Info, PlayCircleFilled } from "@clockwise/icons";
import { FlexStatus } from "@clockwise/schema/v2";
import { ToggleOffFlex as ToggleOffFlexIcon } from "@clockwise/web-commons/src/components/svgs/ToggleOffFlex";
import { ToggleOnFlex as ToggleOnFlexIcon } from "@clockwise/web-commons/src/components/svgs/ToggleOnFlex";
import { Tooltip } from "@clockwise/web-commons/src/ui/tooltip";
import {
  EventType,
  useUpdateActiveEvent,
} from "@clockwise/web-commons/src/util/ActiveEventContext";
import { TrackingEvents, useTracking } from "@clockwise/web-commons/src/util/analytics.util";
import { AssistantOutlined } from "@material-ui/icons";
import { Skeleton } from "@material-ui/lab";
import classNames from "classnames";
import React from "react";
import { Divider } from "../../calendar-popover/atoms/Divider";
import { ListItemButton } from "../../calendar-popover/atoms/ListItemButton";
import { useToggleFlexibility } from "../../hooks/useToggleFlexibility";
import { useUnpauseEvent } from "../../hooks/useUnpauseEvent";
import { eventToast } from "../../notifiation-event/EventToast";

const ToggleOnFlex = () => {
  return <ToggleOnFlexIcon />;
};

const ToggleOffFlex = () => {
  return <ToggleOffFlexIcon />;
};

export const FlexQuickAction = ({
  externalEventId,
  truncatedTitle,
  calendarId,
  loading,
  refetch,
  isFlexible,
  flexStatus,
  showFlexQuickAction,
}: {
  externalEventId: string;
  truncatedTitle: string;
  calendarId: string;
  loading: boolean;
  refetch: () => void;
  isFlexible: boolean;
  flexStatus: FlexStatus | undefined;
  showFlexQuickAction: boolean;
}) => {
  const track = useTracking();
  const { update: toggleFlexibility } = useToggleFlexibility(externalEventId);
  const { update: unpauseEvent } = useUnpauseEvent(externalEventId);
  const updateActiveEvent = useUpdateActiveEvent();

  const meetingIsNotFlex = !isFlexible;

  const handleViewEvent = () => {
    updateActiveEvent({
      calendarId,
      externalEventId,
      type: EventType.Event,
    });
  };

  const onToggleFlexibility = () => {
    if (meetingIsNotFlex) {
      track(TrackingEvents.EVENT_POPOVER.FLEXIBILITY_TOGGLED, {
        flex: true,
      });
      void toggleFlexibility({
        variables: { toggleToOn: true },
        onCompleted: () => {
          refetch();
          eventToast.success({
            operation: "EDIT_DETAILS",
            title: truncatedTitle, // DevNote: full title can be passed here
            onViewEvent: handleViewEvent,
          });
        },
        onError: () => {
          eventToast.error({
            operation: "EDIT_DETAILS",
            title: truncatedTitle, // DevNote: full title can be passed here
            onViewEvent: handleViewEvent,
          });
        },
      });
    } else {
      track(TrackingEvents.EVENT_POPOVER.FLEXIBILITY_TOGGLED, {
        flex: false,
      });
      void toggleFlexibility({
        variables: { toggleToOn: false },
        onCompleted: () => {
          refetch();
          eventToast.success({
            operation: "EDIT_DETAILS",
            title: truncatedTitle, // DevNote: full title can be passed here
            onViewEvent: handleViewEvent,
          });
        },
        onError: () => {
          eventToast.error({
            operation: "EDIT_DETAILS",
            title: truncatedTitle, // DevNote: full title can be passed here
            onViewEvent: handleViewEvent,
          });
        },
      });
    }
  };

  const handleUnpauseEvent = () => {
    void unpauseEvent({
      onCompleted: () => {
        eventToast.success({
          operation: "EDIT_DETAILS",
          title: truncatedTitle, // DevNote: full title can be passed here
          onViewEvent: handleViewEvent,
        });
      },
      onError: () => {
        eventToast.error({
          operation: "EDIT_DETAILS",
          title: truncatedTitle, // DevNote: full title can be passed here
          onViewEvent: handleViewEvent,
        });
      },
    });
  };

  if (loading)
    return (
      <div className="cw-py-1 cw-my-1 cw-px-1 cw-flex cw-items-center cw-gap-2">
        <Skeleton variant="circle" height={16} width={16} className="cw-ml-[2px]" />
        <Skeleton variant="rect" height={24} className="cw-flex-grow cw-rounded-md" />
        <ToggleOffFlexIcon opacity={0.25} />
      </div>
    );

  return (
    showFlexQuickAction && (
      <>
        <Divider />
        <ListItemButton
          startIcon={AssistantOutlined}
          endIcon={meetingIsNotFlex ? ToggleOffFlex : ToggleOnFlex}
          onClick={() => {
            onToggleFlexibility();
          }}
        >
          {meetingIsNotFlex ? "Mark as flexible" : " Flexible meeting"}
        </ListItemButton>
        {flexStatus === FlexStatus.Paused && (
          <div>
            <ListItemButton
              startIcon={PlayCircleFilled}
              onClick={() => {
                handleUnpauseEvent();
              }}
            >
              <div className={classNames("cw-flex cw-flex-row cw-items-center cw-space-x-1")}>
                <div>{FlexStatus.Paused ? "Let it move again" : "Event is Flexible"}</div>
                <Tooltip
                  body={
                    FlexStatus.Paused
                      ? "This meeting was manually rescheduled to a specific time. Clockwise will not move it."
                      : "This meeting is flexible and will move as needed."
                  }
                  placement="bottom"
                  showArrow={false}
                  width={250}
                >
                  <Info className="cw-w-4 cw-h-4 cw-mb-[-4px] cw-cursor-pointer cw-text-subtle" />
                </Tooltip>
              </div>
            </ListItemButton>
          </div>
        )}
      </>
    )
  );
};
